import React, {useCallback, useMemo, useState} from 'react';

import {CompleteMark} from 'src/components/complete-mark';
import {RowNumbered} from 'src/components/row-numbered';

import {Button, FlatButton} from '@pb/kit-component-button';
import {Dialog, DialogFooter, DialogHeader, DialogMain} from '@pb/kit-component-dialog-v2';
import {EllipsisTextHint} from '@pb/kit-component-ellipsis-text-hint';
import {Group} from '@pb/kit-component-group';
import Icon, {Icons} from '@pb/kit-component-icon';
import {Message} from '@pb/kit-component-message';
import {PaginationStripe} from '@pb/kit-component-pagination';
import {Body, Cell, Head, Row, Table} from '@pb/kit-component-table';

import type {MigrationRelationResult} from 'src/openapi/admin/models/MigrationRelationResult';
import type {Pagination} from 'src/openapi/admin/models/Pagination';

type Props = {
    shown: boolean,
    results: MigrationRelationResult[] | null,
    onClose: () => void,
    onDownload?: () => void,
};

const MODAL_WIDTH = 1060;

export const RolesGrantsResourcesImportResultModal = ({
    shown,
    results,
    onClose,
    onDownload,
}: Props) => {
    const shouldRender = shown && !!results;
    const [pagination, setPagination] = useState<Pagination>({currentPage: 1, pageSize: 10});
    const safeResults = useMemo(() => results ?? [], [results]);

    const {successfulCount, failedCount} = useMemo(() => {
        const total = safeResults.length;
        const successful = safeResults.filter(
            relation => !relation.errors || relation.errors.length === 0,
        ).length;

        return {
            successfulCount: successful,
            failedCount: total - successful,
        };
    }, [safeResults]);

    const handlePaginationChange = useCallback((next: Pagination) => {
        setPagination(next);
    }, []);

    const paginatedResults = useMemo(() => {
        const start = (pagination.currentPage - 1) * pagination.pageSize;
        return safeResults.slice(start, start + pagination.pageSize);
    }, [safeResults, pagination]);

    const totalCount = safeResults.length;

    const hasFailures = failedCount > 0;
    const statusMessage = hasFailures
        ? `Completed with ${failedCount} error${failedCount === 1 ? '' : 's'}`
        : `Completed successfully ${successfulCount} item${successfulCount === 1 ? '' : 's'}`;
    const messageColor = hasFailures ? 'warning' : 'success';

    if (!shouldRender || !results) {
        return null;
    }

    return (
        <Dialog shown={shouldRender} setShown={onClose} wrapperProps={{style: {width: MODAL_WIDTH}}}>
            <DialogHeader onClose={onClose}>
                Import Entitlements structure
            </DialogHeader>
            <DialogMain>
                <Message color={messageColor} style={{marginBottom: 'var(--size-m)'}}>
                    {statusMessage}
                </Message>
                <Group direction="v" gap="m" style={{padding: 24}}>
                    <Group justify="space-between" alignItems="center">
                        <b>Selected entitlements</b>
                        {onDownload ? (
                            <FlatButton onClick={onDownload} endIcon={<Icon type={Icons.DOWNLOAD}/>}>
                                Download
                            </FlatButton>
                        ) : null}
                    </Group>
                    <Table borderedGrid={true}>
                        <Head>
                            <Cell width={60}><strong>#</strong></Cell>
                            <Cell width={180}>Role</Cell>
                            <Cell width={220}>Resources</Cell>
                            <Cell width={220}>Grant Name</Cell>
                            <Cell width={120}>Grant Status</Cell>
                            <Cell width={160}>Action Status</Cell>
                        </Head>
                        <Body>
                            {paginatedResults.map((relation: MigrationRelationResult, index: number) => {
                                const grantStatusLabel = relation.grantStatus
                                    ? relation.grantStatus.charAt(0) + relation.grantStatus.slice(1).toLowerCase()
                                    : '';
                                const absoluteIndex = (pagination.currentPage - 1) * pagination.pageSize + index;

                                return (
                                    <Row
                                        key={`${relation.roleExternalId}-${relation.resourceExternalId}-${relation.grantExternalId}-${index}`}>
                                        <Cell width={60}>
                                            <RowNumbered
                                                maxRowNumber={totalCount}
                                                placeholder={absoluteIndex + 1}
                                            />
                                        </Cell>
                                        <Cell width={180}>
                                            <EllipsisTextHint title={relation.roleName || ''}>
                                                {relation.roleName || ''}
                                            </EllipsisTextHint>
                                        </Cell>
                                        <Cell width={220}>
                                            <EllipsisTextHint title={relation.resourceName}>
                                                {relation.resourceName}
                                            </EllipsisTextHint>
                                        </Cell>
                                        <Cell width={220}>
                                            <EllipsisTextHint title={relation.grantName}>
                                                {relation.grantName}
                                            </EllipsisTextHint>
                                        </Cell>
                                        <Cell width={120}>
                                            <EllipsisTextHint title={grantStatusLabel}>
                                                {grantStatusLabel}
                                            </EllipsisTextHint>
                                        </Cell>
                                        <Cell width={160}>
                                            <CompleteMark errors={relation.errors}/>
                                        </Cell>
                                    </Row>
                                );
                            })}
                        </Body>
                    </Table>
                    <PaginationStripe
                        currentPage={pagination.currentPage}
                        pageSize={pagination.pageSize}
                        totalCount={totalCount}
                        onPaginationChange={handlePaginationChange}
                        goToPage={true}
                        sizeSwitcherPosition="LEFT"
                        sizeSwitcherType="ONLY_PAGE_SIZE"
                        sizes={[5, 10]}
                    />
                </Group>
            </DialogMain>
            <DialogFooter>
                <Group>
                    <Button variant="Type.PRIMARY" onClick={onClose}>
                        Close
                    </Button>
                </Group>
            </DialogFooter>
        </Dialog>
    );
};
